import { useState, useMemo, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import NewsBanner from './components/NewsBanner';
import NewsTabs from './components/NewsTabs';
import NewsList from './components/NewsList';
import { supabase } from '../../lib/supabase';
import type { NewsRow } from '../../lib/supabase';
import type { NewsCategory } from '../../mocks/news';
import { newsCategories } from '../../mocks/news';

export default function NewsPage() {
  const [activeTab, setActiveTab] = useState<NewsCategory>('全部');
  const [newsList, setNewsList] = useState<NewsRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('news')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('date', { ascending: false });
      setNewsList(data ?? []);
      setLoading(false);
    };
    fetchNews();
  }, []);

  const filtered = useMemo(() =>
    activeTab === '全部'
      ? newsList
      : newsList.filter(n => n.category === activeTab),
    [activeTab, newsList]
  );

  const counts = useMemo(() => {
    const map: Record<string, number> = { '全部': newsList.length };
    newsCategories.slice(1).forEach(cat => {
      map[cat] = newsList.filter(n => n.category === cat).length;
    });
    return map;
  }, [newsList]);

  const latest = newsList.slice(0, 4);

  // Convert NewsRow to NewsItem shape for compatibility
  const filteredAsItems = filtered.map(n => ({
    id: n.id,
    category: n.category as Exclude<NewsCategory, '全部'>,
    date: n.date,
    title: n.title,
    excerpt: n.excerpt,
    content: n.content,
    tags: n.tags,
  }));

  const latestAsItems = latest.map(n => ({
    id: n.id,
    category: n.category as Exclude<NewsCategory, '全部'>,
    date: n.date,
    title: n.title,
    excerpt: n.excerpt,
    content: n.content,
    tags: n.tags,
  }));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">
        <NewsBanner />

        <section className="max-w-6xl mx-auto px-4 py-12">
          {loading ? (
            <div className="py-24 text-center">
              <i className="ri-loader-4-line text-3xl text-gray-300 animate-spin block mb-2" />
              <p className="text-sm text-gray-400">載入中...</p>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-7 bg-[#1a73b5]" />
                  <h2 className="text-xl font-bold text-gray-800">
                    {activeTab === '全部' ? '全部消息' : activeTab}
                  </h2>
                  <span className="text-sm text-gray-400 ml-1">（{filtered.length} 則）</span>
                </div>
                <NewsTabs active={activeTab} counts={counts} onChange={cat => setActiveTab(cat)} />
                <NewsList items={filteredAsItems} />
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-64 flex-shrink-0 flex flex-col gap-6">
                <div className="border border-gray-100 overflow-hidden">
                  <div className="bg-[#1a2535] px-4 py-3 flex items-center gap-2">
                    <i className="ri-time-line text-[#4fc3f7]" />
                    <span className="text-white text-sm font-bold tracking-wider">最新消息</span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {latestAsItems.map(item => (
                      <div key={item.id} className="px-4 py-3 hover:bg-[#f9fbff] cursor-pointer transition-colors group">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs text-gray-400">{item.date.replace(/-/g, '.')}</span>
                        </div>
                        <p className="text-sm text-gray-700 group-hover:text-[#1a73b5] transition-colors line-clamp-2 leading-snug">
                          {item.title}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-100 overflow-hidden">
                  <div className="bg-[#1a2535] px-4 py-3 flex items-center gap-2">
                    <i className="ri-folder-3-line text-[#4fc3f7]" />
                    <span className="text-white text-sm font-bold tracking-wider">消息分類</span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {newsCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setActiveTab(cat)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer transition-colors
                          ${activeTab === cat ? 'text-[#1a73b5] font-semibold bg-[#f0f6ff]' : 'text-gray-600 hover:text-[#1a73b5] hover:bg-[#f9fbff]'}`}
                      >
                        <div className="flex items-center gap-2">
                          <i className={`ri-arrow-right-s-line text-base ${activeTab === cat ? 'text-[#1a73b5]' : 'text-gray-300'}`} />
                          {cat}
                        </div>
                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                          {counts[cat] ?? 0}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-[#1a73b5] px-5 py-6 text-white text-center">
                  <i className="ri-customer-service-2-line text-3xl mb-3 block" />
                  <p className="font-bold text-base mb-1">有產品問題？</p>
                  <p className="text-xs text-white/80 mb-4 leading-relaxed">
                    專業顧問團隊隨時為您<br />提供選型與技術諮詢
                  </p>
                  <a
                    href="/contact"
                    className="inline-block bg-white text-[#1a73b5] text-sm font-bold px-5 py-2 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    立即聯絡
                  </a>
                </div>
              </aside>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
